import type { ReportColumn } from '../../types';

/** One place decides how every value type is shown and exported. */
export function displayCell(type: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  switch (type) {
    case 'bool':
      return value ? 'Yes' : 'No';
    case 'money': {
      const n = Number(value);
      return Number.isFinite(n)
        ? `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : String(value);
    }
    case 'datetime': {
      const d = new Date(String(value));
      return Number.isNaN(d.getTime())
        ? String(value)
        : d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
    }
    case 'date':
    case 'number':
    case 'text':
    default:
      return String(value);
  }
}

/** Excel/CSV want native types where possible so sorting and sums work. */
function exportCell(type: string, value: unknown): string | number {
  if (value === null || value === undefined || value === '') return '';
  if (type === 'money' || type === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? n : String(value);
  }
  if (type === 'bool') return value ? 'Yes' : 'No';
  return String(value);
}

type Row = Record<string, unknown>;

function matrix(columns: ReportColumn[], rows: Row[]): (string | number)[][] {
  const header = columns.map((c) => c.label);
  const body = rows.map((row) => columns.map((c) => exportCell(c.type, row[c.key])));
  return [header, ...body];
}

function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export async function exportXlsx(name: string, columns: ReportColumn[], rows: Row[]): Promise<void> {
  // Loaded on demand so the ~800KB spreadsheet engine never ships in the
  // initial bundle — only when someone actually exports Excel.
  const XLSX = await import('xlsx');
  const ws = XLSX.utils.aoa_to_sheet(matrix(columns, rows));
  ws['!cols'] = columns.map((c) => ({ wch: Math.min(Math.max(c.label.length + 2, 12), 40) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, `${name}_${stamp()}.xlsx`);
}

export function exportCsv(name: string, columns: ReportColumn[], rows: Row[]): void {
  const esc = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = matrix(columns, rows).map((r) => r.map(esc).join(',')).join('\r\n');
  // A BOM so Excel opens UTF-8 (₹, names) correctly.
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}_${stamp()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
